import React, { useEffect, useRef, useState } from 'react';
import s from './AddProductReview.module.scss';
import ProfileMenuHeader from 'modules/app/components/ProfileMenu/ProfileMenuHeader';
import { useNavigate, useParams } from 'react-router-dom';
import TextArea from 'components/shared/TextArea';
import { ReactComponent as GreyStar } from 'images/new-icons/grey-star-big.svg';
import { ReactComponent as HalfStar } from 'images/new-icons/half-yellow-star-big.svg';
import { ReactComponent as Star } from 'images/new-icons/yellow-star-big.svg';
import { ReactComponent as IconUploadBg } from 'images/new-icons/upload-image.svg';
import Button from 'components/shared/Button';
import useAddProductReview from 'sdk/hooks/shop/useAddProductReview';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { useGetMyUser } from 'sdk/hooks/user/useGetMyUser';

const AddProductReview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [hoveredRating, setHoveredRating] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: isAuthData } = useIsAuth();

  const { data: userData } = useGetMyUser(isAuthData?.user?.id || '');

  const [text, setText] = useState<string>('');
  const [rank, setRank] = useState<number>(0);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [isAnon, setIsAnon] = useState<boolean>(false);

  const [isMediaLoading, setIsMediaLoading] = useState(false);

  const addReview = useAddProductReview(text, rank, id, mediaUrls, userData?.username, isAnon);

  const handleMediaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, starIndex: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const width = rect.width;

    if (mouseX < width / 2) {
      setHoveredRating(starIndex - 0.5);
    } else {
      setHoveredRating(starIndex);
    }
  };

  const renderStars = () => {
    const stars: JSX.Element[] = [];
    for (let i = 1; i <= 5; i++) {
      const currentRating = hoveredRating || rank;
      stars.push(
        <div
          key={i}
          onMouseMove={e => handleMouseMove(e, i)}
          onMouseEnter={() => setHoveredRating(i)}
          onMouseLeave={() => {
            setHoveredRating(0);
          }}
          onClick={() => {
            setRank(hoveredRating || i);
          }}
          className={s.star}
        >
          {currentRating >= i ? <Star /> : currentRating >= i - 0.5 ? <HalfStar /> : <GreyStar />}
        </div>,
      );
    }
    return stars;
  };

  const renderMedia = () => {
    if (isMediaLoading) {
      return <LoadingSpinner />;
    }

    return mediaUrls.map((url, index) => {
      if (url.endsWith('.mp4') || url.includes('video')) {
        return (
          <video key={index} controls playsInline muted className={s.uploadedVideo}>
            <source src={url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      } else {
        return <img key={index} src={url} alt={`uploaded-${index}`} className={s.uploadedImage} />;
      }
    });
  };

  const uploadToCloudinary = async file => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'ml_default');

    const uploadEndpoint = file.type.startsWith('video')
      ? `${process.env.REACT_APP_CLOUDINARY_URL}/video/upload`
      : `${process.env.REACT_APP_CLOUDINARY_URL}/image/upload`;
    setIsMediaLoading(true);
    try {
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        return data.secure_url;
      } else {
        console.error('Ошибка загрузки в Cloudinary:', await response.json());
        return null;
      }
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      return null;
    } finally {
      setIsMediaLoading(false);
    }
  };

  const uploadMediaFiles = async files => {
    const uploadPromises = files.map(file => uploadToCloudinary(file));
    const urls = await Promise.all(uploadPromises);
    return urls.filter(url => url !== null);
  };

  const onBgUpload = async e => {
    const files = e.target.files;
    setIsMediaLoading(true);
    if (files) {
      try {
        const uploadedUrls = await uploadMediaFiles(Array.from(files));
        setMediaUrls(prevUrls => [...prevUrls, ...uploadedUrls]);
      } catch (err) {
        console.log(err);
      } finally {
        setIsMediaLoading(false);
      }
    }
  };

  const LoadingSpinner = () => (
    <div className={s.spinner}>
      <div className={s.doubleBounce1}></div>
      <div className={s.doubleBounce2}></div>
    </div>
  );

  useEffect(() => {
    if (addReview.isSuccess) {
      navigate(-1);
    }
  }, [addReview.isSuccess]);

  return (
    <div className={s.review}>
      <ProfileMenuHeader text="Add review" onClose={() => navigate(-1)} />
      <div className={s.review_form}>
        <div className={s.review_form_field}>
          <div className={s.review_form_field_label}>Review</div>
          <TextArea
            value={text ?? ''}
            placeholder="Enter review"
            onChange={e => setText(e.target?.value)}
          />
        </div>
        <div className={s.review_form_field}>
          <div className={s.review_form_field_label}>Rating</div>
          <div className={s.review_form_field_rating}>{renderStars()}</div>
        </div>
        <div className={s.review_form_field}>
          <div className={s.review_form_field_label}>Add photos / videos</div>
          <div className={s.medias} onClick={handleMediaClick}>
            <form id="bgForm" className={s.bgUploadLabel}>
              <label htmlFor="bgUpload">
                <input
                  type="file"
                  name="file"
                  id="bgUpload"
                  className={s.bgUploadInput}
                  onChange={onBgUpload}
                  accept={'image/*, video/*'}
                  multiple
                  ref={fileInputRef}
                />
                <IconUploadBg />
              </label>
            </form>
            <div className={s.review_media}>{renderMedia()}</div>
          </div>
        </div>
        <div className={s.review_form_anon}>
          <input type="checkbox" onChange={(e: any) => setIsAnon(e.target.checked)} />
          <div>Leave A Review Anonymously</div>
        </div>
      </div>
      <Button
        wide
        darkGreen
        className={s.button}
        loading={isMediaLoading}
        disabled={isMediaLoading}
        onClick={() => {
          addReview.mutate();
        }}
      >
        Post your review
      </Button>
    </div>
  );
};

export default AddProductReview;
